import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { throttle } from 'lodash-es'; // 引入lodash的节流函数
import { useDebounceEffect } from 'ahooks';

// 定义拖拽方向的类型，包括8个方向和移动
export type DragDirection =
  | 'n'
  | 'ne'
  | 'e'
  | 'se'
  | 's'
  | 'sw'
  | 'w'
  | 'nw'
  | 'move';

// 定义组件属性接口
interface StretchableProps {
  children: React.ReactNode; // 组件内容
  defaultWidth?: number; // 默认宽度
  defaultHeight?: number; // 默认高度
  defaultX?: number; // 默认X坐标
  defaultY?: number; // 默认Y坐标
  minWidth?: number; // 最小宽度
  minHeight?: number; // 最小高度
  maxWidth?: number; // 最大宽度
  maxHeight?: number; // 最大高度
  onResize?: (width: number, height: number) => void; // 尺寸变化回调
  onResizeStart?: () => void; // 拉伸开始回调
  onMove?: (x: number, y: number) => void; // 位置变化回调
  style?: React.CSSProperties; // 自定义样式
  contentStyle?: React.CSSProperties; // 内容区域样式
  enabledEdges?: DragDirection[]; // 启用的边缘
  safeArea?: {
    // 安全边距
    top?: number; // 顶部安全距离
    right?: number; // 右侧安全距离
    bottom?: number; // 底部安全距离
    left?: number; // 左侧安全距离
  };
  className?: string;
}

/*
@example 一个可以调整大小和拖动的容器。
  <DragResizeBox 
        defaultWidth={300}
        defaultHeight={200}
        defaultX={0}
        defaultY={0}
        minWidth={150}
        minHeight={150}
        safeArea={{ top: 50, right: 50, bottom: 50, left: 50 }}
      >
        <div>
          <h3>内容区域</h3>
          <p>这是一个可拖拽和拉伸的面板</p>
          <p>尝试拖动标题栏移动面板</p>
          <p>尝试拖动边缘或角落调整大小</p>
        </div>
      </DragResizeBox >
*/
const DragResizeBox = forwardRef(
  (
    {
      children,
      defaultWidth = 300, // 默认宽度300px
      defaultHeight = 200, // 默认高度200px
      defaultX = 0, // 默认X位置0
      defaultY = 0, // 默认Y位置0
      minWidth = 100, // 最小宽度100px
      minHeight = 100, // 最小高度100px
      maxWidth = Infinity, // 最大宽度无限制
      maxHeight = Infinity, // 最大高度无限制
      onResize, // 尺寸变化回调函数
      onResizeStart, // 拉伸开始回调函数
      onMove, // 位置变化回调函数
      style, // 自定义样式
      contentStyle, // 内容区域样式
      enabledEdges = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'], // 默认启用所有边缘
      safeArea = {}, // 安全边距默认空对象
      className,
    }: StretchableProps,
    ref: any,
  ) => {
    // 容器元素的引用
    const containerRef = useRef<HTMLDivElement | null>(null);

    // 组件尺寸状态
    const [size, setSize] = useState({
      width: defaultWidth, // 当前宽度
      height: defaultHeight, // 当前高度
    });

    // 组件位置状态
    const [position, setPosition] = useState({
      x: defaultX, // 当前X坐标
      y: defaultY, // 当前Y坐标
    });

    // 是否正在拖拽的状态
    const [isDragging, setIsDragging] = useState(false);
    const isDraggingRef = useRef(false);

    // 当前拖拽方向的状态
    const [dragDirection, setDragDirection] = useState<DragDirection | null>(
      null,
    );

    // 是否正在拉伸的状态
    const [isResizing, setIsResizing] = useState(false);

    // 拖拽信息引用，记录拖拽开始时的状态
    const dragInfo = useRef({
      startX: 0, // 鼠标按下时的X坐标
      startY: 0, // 鼠标按下时的Y坐标
      startWidth: 0, // 拖拽开始时的宽度
      startHeight: 0, // 拖拽开始时的高度
      startLeft: 0, // 拖拽开始时的left位置
      startTop: 0, // 拖拽开始时的top位置
    });

    // 解构安全边距，如果没有提供则默认为0
    const {
      top: safeTop = 0, // 顶部安全距离，默认0
      right: safeRight = 0, // 右侧安全距离，默认0
      bottom: safeBottom = 0, // 底部安全距离，默认0
      left: safeLeft = 0, // 左侧安全距离，默认0
    } = safeArea;

    /**
     * 应用安全边距限制
     * @param x 当前X坐标
     * @param y 当前Y坐标
     * @param width 当前宽度
     * @param height 当前高度
     * @returns 受安全边距约束后的位置和尺寸
     */
    const applySafeAreaConstraints = useCallback(
      (x: number, y: number, width: number, height: number) => {
        // 计算最大允许的x和y坐标
        const maxX = window.innerWidth - width - safeRight;
        const maxY = window.innerHeight - height - safeBottom;

        // 限制x和y在安全区域内
        const constrainedX = Math.max(safeLeft, Math.min(maxX, x));
        const constrainedY = Math.max(safeTop, Math.min(maxY, y));

        // 如果宽度或高度会导致超出安全区域，则需要调整
        let constrainedWidth = width;
        let constrainedHeight = height;

        // 检查右边是否超出安全区域
        if (constrainedX + constrainedWidth > window.innerWidth - safeRight) {
          constrainedWidth = window.innerWidth - safeRight - constrainedX;
        }

        // 检查底部是否超出安全区域
        if (
          constrainedY + constrainedHeight >
          window.innerHeight - safeBottom
        ) {
          constrainedHeight = window.innerHeight - safeBottom - constrainedY;
        }

        // 确保调整后的尺寸不小于最小尺寸
        constrainedWidth = Math.max(minWidth, constrainedWidth);
        constrainedHeight = Math.max(minHeight, constrainedHeight);

        return {
          x: constrainedX,
          y: constrainedY,
          width: constrainedWidth,
          height: constrainedHeight,
        };
      },
      [safeTop, safeRight, safeBottom, safeLeft, minWidth, minHeight],
    );

    // 当浏览器窗口resize时，防止Copilot超出边界
    useEffect(() => {
      const handleResize = throttle(() => {
        const newSize = {
          width: window.innerWidth,
          height: window.innerHeight,
        };

        // 如果当前拖拽框超出新边界，立即修正位置
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          // 使用阈值判断：只有超出边界超过阈值时才调整
          const THRESHOLD = 2;
          const needsAdjust = 
            rect.right > ((newSize.width - safeArea.right!) + THRESHOLD) ||
            rect.bottom > ((newSize.height - safeArea.left!) + THRESHOLD) ||
            rect.left < safeArea.left! ||
            rect.top < safeArea.top!;

          if (needsAdjust) {
            const newX = Math.max(
              safeArea.left!,
              Math.min(rect.left, newSize.width - rect.width - safeArea.right!),
            );

            const newY = Math.max(
              safeArea.top!,
              Math.min(
                rect.top,
                newSize.height - rect.height - safeArea.bottom!,
              ),
            );
            containerRef.current.style.left = `${newX}px`;
            containerRef.current.style.top = `${newY}px`;
          }
        }
      }, 200);

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    /**
     * 判断是否已达到最小尺寸
     * @returns 是否达到最小尺寸
     */
    const isMinSize = useCallback(
      () => size.width <= minWidth || size.height <= minHeight,
      [size.width, size.height, minWidth, minHeight],
    );

    /**
     * 鼠标按下事件处理
     * @param direction 拖拽方向
     * @param e 鼠标事件
     */
    const handleMouseDown = useCallback(
      (direction: DragDirection, e: React.MouseEvent) => {
        e.preventDefault(); // 阻止默认行为
        e.stopPropagation(); // 阻止事件冒泡

        if (!containerRef.current) return; // 如果容器不存在则返回

        // 检查当前方向是否被启用
        if (!enabledEdges.includes(direction) && direction !== 'move') {
          return;
        }

        // 获取容器当前的位置和尺寸
        const rect = containerRef.current.getBoundingClientRect();
        // 记录拖拽开始时的信息
        dragInfo.current = {
          startX: e.clientX,
          startY: e.clientY,
          startWidth: rect.width,
          startHeight: rect.height,
          startLeft: rect.left,
          startTop: rect.top,
        };
        // 设置拖拽方向和状态
        isDraggingRef.current = true;
        setDragDirection(direction);
        setIsDragging(true);
        setIsResizing(direction !== 'move');

        // 拉伸开始时触发回调（用于层级提升等）
        if (direction !== 'move') {
          onResizeStart?.();
        }

        // 根据拖拽方向设置鼠标样式
        let cursor = '';
        switch (direction) {
          case 'n':
          case 's':
            cursor = 'ns-resize'; // 南北方向调整
            break;
          case 'e':
          case 'w':
            cursor = 'ew-resize'; // 东西方向调整
            break;
          case 'ne':
          case 'sw':
            cursor = 'nesw-resize'; // 东北-西南方向调整
            break;
          case 'nw':
          case 'se':
            cursor = 'nwse-resize'; // 西北-东南方向调整
            break;
          case 'move':
            cursor = 'move';
            break;
        }
        // 设置全局鼠标样式和禁用文本选择
        document.body.style.cursor = cursor;
        document.body.style.userSelect = 'none';
      },
      [isMinSize, enabledEdges, onResizeStart],
    );

    /**
     * 鼠标移动事件处理（使用节流函数优化性能）
     */
    const handleMouseMove = useCallback(
      throttle((e: MouseEvent) => {
        // 如果没有拖拽或没有方向或容器不存在则返回（ref 同步失效，避免 mouseup 后节流尾调用仍移动）
        if (!isDraggingRef.current || !dragDirection || !containerRef.current) {
          return;
        }
        if (e.buttons === 0) return;
        // 获取拖拽开始时的信息
        const { startX, startY, startWidth, startHeight, startLeft, startTop } =
          dragInfo.current;
        const deltaX = e.clientX - startX;
        const deltaY = e.clientY - startY;
        // 初始化新的尺寸和位置
        let newWidth = startWidth;
        let newHeight = startHeight;
        let newLeft = startLeft;
        let newTop = startTop;

        // 先计算新的尺寸和位置
        if (isResizing) {
          switch (dragDirection) {
            case 'e': // 东边调整
              newWidth = Math.max(
                minWidth,
                Math.min(maxWidth, startWidth + deltaX),
              );
              break;
            case 'w': // 西边调整
              newWidth = Math.max(
                minWidth,
                Math.min(maxWidth, startWidth - deltaX),
              );
              newLeft = startLeft + (startWidth - newWidth);
              break;
            case 's': // 南边调整
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, startHeight + deltaY),
              );
              break;
            case 'n': // 北边调整
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, startHeight - deltaY),
              );
              newTop = startTop + (startHeight - newHeight);
              break;
            case 'ne': // 东北角调整
              newWidth = Math.max(
                minWidth,
                Math.min(maxWidth, startWidth + deltaX),
              );
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, startHeight - deltaY),
              );
              newTop = startTop + (startHeight - newHeight);
              break;
            case 'nw': // 西北角调整
              newWidth = Math.max(
                minWidth,
                Math.min(maxWidth, startWidth - deltaX),
              );
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, startHeight - deltaY),
              );
              newLeft = startLeft + (startWidth - newWidth);
              newTop = startTop + (startHeight - newHeight);
              break;
            case 'se': // 东南角调整
              newWidth = Math.max(
                minWidth,
                Math.min(maxWidth, startWidth + deltaX),
              );
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, startHeight + deltaY),
              );
              break;
            case 'sw': // 西南角调整
              newWidth = Math.max(
                minWidth,
                Math.min(maxWidth, startWidth - deltaX),
              );
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, startHeight + deltaY),
              );
              newLeft = startLeft + (startWidth - newWidth);
              break;
          }
        } else if (dragDirection === 'move') {
          newLeft += deltaX;
          newTop += deltaY;
        }

        // 检查是否超出安全区域
        const maxX = window.innerWidth - safeRight;
        const maxY = window.innerHeight - safeBottom;

        // 检查是否已经到达边界
        const isAtRightBoundary = newLeft + newWidth >= maxX;
        const isAtLeftBoundary = newLeft <= safeLeft;
        const isAtBottomBoundary = newTop + newHeight >= maxY;
        const isAtTopBoundary = newTop <= safeTop;

        // 如果已经到达边界，则阻止进一步改变尺寸
        if (isResizing) {
          const { width: curWidth, height: curHeight, left: curLeft, top: curTop } =
            containerRef.current.getBoundingClientRect();
          switch (dragDirection) {
            case 'e':
              if (isAtRightBoundary) newWidth = curWidth;
              break;
            case 'w':
              if (isAtLeftBoundary) {
                newWidth = curWidth;
                newLeft = curLeft;
              }
              break;
            case 's':
              if (isAtBottomBoundary) newHeight = curHeight;
              break;
            case 'n':
              if (isAtTopBoundary) {
                newHeight = curHeight;
                newTop = curTop;
              }
              break;
            case 'ne':
              if (isAtRightBoundary || isAtTopBoundary) {
                newWidth = isAtRightBoundary ? curWidth : newWidth;
                newHeight = isAtTopBoundary ? curHeight : newHeight;
                newTop = isAtTopBoundary ? curTop : newTop;
              }
              break;
            case 'nw':
              if (isAtLeftBoundary || isAtTopBoundary) {
                newWidth = isAtLeftBoundary ? curWidth : newWidth;
                newHeight = isAtTopBoundary ? curHeight : newHeight;
                newLeft = isAtLeftBoundary ? curLeft : newLeft;
                newTop = isAtTopBoundary ? curTop : newTop;
              }
              break;
            case 'se':
              if (isAtRightBoundary || isAtBottomBoundary) {
                newWidth = isAtRightBoundary ? curWidth : newWidth;
                newHeight = isAtBottomBoundary ? curHeight : newHeight;
              }
              break;
            case 'sw':
              if (isAtLeftBoundary || isAtBottomBoundary) {
                newWidth = isAtLeftBoundary ? curWidth : newWidth;
                newHeight = isAtBottomBoundary ? curHeight : newHeight;
                newLeft = isAtLeftBoundary ? curLeft : newLeft;
              }
              break;
          }
        }

        // 应用安全边距限制
        const constrained = applySafeAreaConstraints(
          newLeft,
          newTop,
          newWidth,
          newHeight,
        );

        // 更新容器样式
        containerRef.current.style.width = `${constrained.width}px`;
        containerRef.current.style.height = `${constrained.height}px`;
        containerRef.current.style.left = `${constrained.x}px`;
        containerRef.current.style.top = `${constrained.y}px`;

        // 更新状态
        if (dragDirection === 'move') {
          // 如果是移动操作，只更新位置
          setPosition({ x: constrained.x, y: constrained.y });
        } else {
          // 如果是调整大小操作，更新尺寸和位置
          setSize({ width: constrained.width, height: constrained.height });
          setPosition({ x: constrained.x, y: constrained.y });
        }
      }, 16),
      [
        dragDirection,
        minWidth,
        maxWidth,
        minHeight,
        maxHeight,
        isResizing,
        applySafeAreaConstraints,
        safeRight,
        safeBottom,
        safeLeft,
        safeTop,
      ],
    );

    /**
     * 鼠标释放事件处理
     */
    const handleMouseUp = useCallback(() => {
      if (!isDraggingRef.current) return; // 如果没有拖拽则返回

      isDraggingRef.current = false;
      handleMouseMove.cancel();
      // 重置拖拽状态
      setIsDragging(false);
      setIsResizing(false);
      setDragDirection(null);
      // 恢复鼠标样式和文本选择
      document.body.style.cursor = '';
      document.body.style.userSelect = '';

      // 调用回调函数
      const rect = containerRef.current?.getBoundingClientRect();
      if (dragDirection === 'move') {
        onMove?.(rect?.left ?? position.x, rect?.top ?? position.y); // 如果是移动操作，调用onMove
      } else {
        onResize?.(rect?.width ?? size.width, rect?.height ?? size.height); // 如果是调整大小操作，调用onResize
        onMove?.(rect?.left ?? position.x, rect?.top ?? position.y); // 同时调用onMove
      }
    }, [dragDirection, onResize, onMove, handleMouseMove]);

    // 监听拖拽状态变化，添加/移除鼠标移动和释放事件
    useEffect(() => {
      if (isDragging) {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
      }

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        handleMouseMove.cancel(); // 取消节流函数
      };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    // 初始化时应用安全边距
    useEffect(() => {
      if (containerRef.current) {
        // 计算受约束的位置和尺寸
        const constrained = applySafeAreaConstraints(
          position.x,
          position.y,
          size.width,
          size.height,
        );

        // 更新容器样式
        containerRef.current.style.width = `${constrained.width}px`;
        containerRef.current.style.height = `${constrained.height}px`;
        containerRef.current.style.left = `${constrained.x}px`;
        containerRef.current.style.top = `${constrained.y}px`;

        // 更新状态
        setSize({ width: constrained.width, height: constrained.height });
        setPosition({ x: constrained.x, y: constrained.y });
      }
    }, []); // 空依赖数组表示只在组件挂载时执行

    useDebounceEffect(
      () => {
        const resizeEvent = new Event('resize');
        window.dispatchEvent(resizeEvent);
      },
      [size.width],
      {
        wait: 300,
      },
    );
  
    /**
     * 渲染可拉伸的边缘手柄
     * @returns 边缘手柄元素数组
     */
    const renderEdgeHandles = () => {
      // 定义所有可拉伸边缘的配置
      const edges = [
        {
          direction: 'n', // 北边
          style: {
            top: 0,
            left: 0,
            right: 0,
            height: '5px',
            cursor: 'ns-resize',
          },
        },
        {
          direction: 'e', // 东边
          style: {
            top: 0,
            right: 0,
            bottom: 0,
            width: '5px',
            cursor: 'ew-resize',
          },
        },
        {
          direction: 's', // 南边
          style: {
            bottom: 0,
            left: 0,
            right: 0,
            height: '5px',
            cursor: 'ns-resize',
          },
        },
        {
          direction: 'w', // 西边
          style: {
            top: 0,
            left: 0,
            bottom: 0,
            width: '5px',
            cursor: 'ew-resize',
          },
        },
        {
          direction: 'ne', // 东北角
          style: {
            top: 0,
            right: 0,
            width: '10px',
            height: '10px',
            cursor: 'nesw-resize',
          },
        },
        {
          direction: 'se', // 东南角
          style: {
            bottom: 0,
            right: 0,
            width: '10px',
            height: '10px',
            cursor: 'nwse-resize',
          },
        },
        {
          direction: 'sw', // 西南角
          style: {
            bottom: 0,
            left: 0,
            width: '10px',
            height: '10px',
            cursor: 'nesw-resize',
          },
        },
        {
          direction: 'nw', // 西北角
          style: {
            top: 0,
            left: 0,
            width: '10px',
            height: '10px',
            cursor: 'nwse-resize',
          },
        },
      ];

      // 过滤出启用的边缘并渲染
      return edges
        .filter((edge) =>
          enabledEdges.includes(edge.direction as DragDirection),
        )
        .map((edge) => (
          <div
            key={edge.direction} // 唯一key
            onMouseDown={(e) =>
              handleMouseDown(edge.direction as DragDirection, e)
            }
            style={{
              position: 'absolute',
              zIndex: edge.direction.length === 1 ? 1100 : 1200, // 角落手柄z-index更高
              ...edge.style,
            }}
          />
        ));
    };

    useImperativeHandle(ref, () => ({
      handleMouseDown,
      dropAreaRef: containerRef,
    }));

    const showMask = isDragging || isResizing;

    // 组件渲染
    return (
      <>
        {/* 遮罩层 */}
        {showMask && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              zIndex: 2002,
            }}
          />
        )}
        <div
          className={className}
          ref={containerRef} // 容器引用
          style={{
            position: 'absolute', // 绝对定位
            width: `${size.width}px`, // 当前宽度
            height: `${size.height}px`, // 当前高度
            left: `${position.x}px`, // 当前X位置
            top: `${position.y}px`, // 当前Y位置
            overflow: 'hidden', // 溢出隐藏
            transform: 'translateZ(0)', // 创建独立层叠上下文，不能删
            ...style, // 合并自定义样式
          }}
        >
          {/* 内容区域 */}
          <div
            style={{
            // padding: '12px', // 内边距
              height: '100%', // 高度100%
              overflow: 'auto', // 溢出滚动
              ...contentStyle, // 合并内容样式
            }}
          >
            {children}
          </div>
          {/* 渲染启用的边缘手柄 */}
          {(!isDragging || isResizing) && renderEdgeHandles()}
        </div>
      </>
    );
  },
);

DragResizeBox.displayName = 'DragResizeBox';
export default DragResizeBox;
